/**
 * ActiveEngagementsBanner.jsx — Iter 66. Rotating banner at the top of a
 * marketplace showing what the founder already has in flight there.
 *
 * "In flight" is everything that isn't delivered or closed, newest update first,
 * so the slide a founder lands on is the thing that most recently moved. Slides
 * auto-advance; hovering or focusing pauses, and the dots let them drive.
 *
 * Deliberately separate from the Iter 61 locked-engagements banner below it:
 * that one is a warning about blocked work, this one is a status ticker. Both
 * can be on screen at once and they say different things.
 */
import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  ArrowRight, CaretLeft, CaretRight, ChatCircleDots, Clock, Pause,
} from '@phosphor-icons/react';
import ProgressDonut from './ProgressDonut';

const ROTATE_MS = 6000;

// Everything that isn't finished. Ordered by the founder-attention it needs so
// the copy per slide can be specific rather than generic.
const IN_FLIGHT = new Set([
  'draft', 'agreement_signed', 'awaiting_quote', 'quote_ready', 'pending_payment', 'active',
]);

const SERVICE_FLOW = 'service_v2';

// Per-status headline + call to action. `you` marks the slides where the ball is
// in the founder's court, which is what earns the amber treatment.
const SLIDE = {
  draft: { tone: 'you', label: 'Agreement waiting', cta: 'Accept agreement',
    blurb: (v) => `Accept the three-party agreement to get ${v} started.` },
  agreement_signed: { tone: 'you', label: 'Documents needed', cta: 'Upload documents',
    blurb: (v) => `${v} is waiting on your documents and company details.` },
  awaiting_quote: { tone: 'wait', label: 'With your partner', cta: 'View progress',
    blurb: (v) => `${v} is reviewing your documents and preparing a final quote.` },
  quote_ready: { tone: 'you', label: 'Quote ready', cta: 'Review quote',
    blurb: (v) => `${v} sent a final quote — approve it to release the work.` },
  pending_payment: { tone: 'you', label: 'Payment due', cta: 'Complete payment',
    blurb: (v) => `Settle the approved quote and ${v} starts immediately.` },
  active: { tone: 'go', label: 'In progress', cta: 'Track delivery',
    blurb: (v) => `${v} is working on your filing. We'll email you at each step.` },
};

const TONES = {
  you:  { border: 'border-warning/40', bg: 'bg-warning/10', chip: 'bg-warning/20 text-warning' },
  wait: { border: 'border-border',     bg: 'bg-surface',    chip: 'bg-text-muted/15 text-text-muted' },
  go:   { border: 'border-success/40', bg: 'bg-success/10', chip: 'bg-success/20 text-success' },
};

const ActiveEngagementsBanner = ({ engagements, category, accent, basePath }) => {
  const [idx, setIdx] = useState(0);
  const [paused, setPaused] = useState(false);

  // Newest update first. Guided rows always carry `updated_at`; legacy intro
  // rows may only have `created_at`, so fall back rather than sorting them last.
  const slides = useMemo(() => (engagements || [])
    .filter((e) => IN_FLIGHT.has(e.status))
    .sort((a, b) => String(b.updated_at || b.created_at || '').localeCompare(String(a.updated_at || a.created_at || ''))),
    [engagements]);

  // Keep the index in range when the list shrinks under us (a slide can vanish
  // mid-rotation once its engagement is delivered).
  useEffect(() => { setIdx((i) => (slides.length ? i % slides.length : 0)); }, [slides.length]);

  useEffect(() => {
    if (paused || slides.length < 2) return;
    const t = setInterval(() => setIdx((i) => (i + 1) % slides.length), ROTATE_MS);
    return () => clearInterval(t);
  }, [paused, slides.length]);

  if (slides.length === 0) return null;

  const e = slides[Math.min(idx, slides.length - 1)];
  const meta = SLIDE[e.status] || SLIDE.active;
  const tone = TONES[meta.tone];
  const isGuided = e.flow === SERVICE_FLOW;
  const pct = e.progress?.pct ?? 0;
  const unread = e.chat?.unread || 0;
  const itemName = e.offering?.catalog_item_name || e.vendor_name || 'your engagement';
  // Guided requests resume inside the wizard; everything else lands on the
  // engagements tab, which is the only place a legacy intro row can be acted on.
  const href = isGuided ? `/startup/services/request/${e.id}` : `${basePath}/engagements`;

  const step = (delta) => {
    setPaused(true);
    setIdx((i) => (i + delta + slides.length) % slides.length);
  };

  return (
    <div
      className={`mb-4 rounded-xl border ${tone.border} ${tone.bg} overflow-hidden`}
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
      onFocus={() => setPaused(true)}
      onBlur={() => setPaused(false)}
      data-testid={`active-engagements-banner-${category}`}
    >
      <div className="p-4 flex items-center gap-4 flex-wrap">
        {/* Progress ring — same number the engagement tile and wizard show. */}
        <ProgressDonut pct={pct} size={62} stroke={6} color={accent}
          testid="active-engagements-banner-donut" />

        <div className="flex-1 min-w-[220px]">
          <div className="flex items-center gap-2 flex-wrap mb-1">
            <span className={`text-[10px] uppercase tracking-wider font-bold px-2 py-0.5 rounded ${tone.chip}`}
              data-testid="active-engagements-banner-status">
              {meta.label}
            </span>
            <span className="text-[11px] uppercase tracking-[0.16em] font-bold text-text-muted">
              {slides.length === 1 ? 'In progress' : `${idx + 1} of ${slides.length} in progress`}
            </span>
            {unread > 0 && (
              <span className="text-[11px] font-semibold inline-flex items-center gap-1" style={{ color: accent }}
                data-testid="active-engagements-banner-unread">
                <ChatCircleDots size={11} weight="fill" /> {unread} new message{unread === 1 ? '' : 's'}
              </span>
            )}
            {paused && slides.length > 1 && (
              <Pause size={10} weight="fill" className="text-text-muted" title="Rotation paused" />
            )}
          </div>
          <div className="text-base font-heading font-bold text-text-primary truncate leading-tight"
            data-testid="active-engagements-banner-title">
            {itemName}
          </div>
          <div className="text-[12.5px] text-text-secondary mt-0.5 leading-snug">
            {meta.blurb(e.vendor_name || 'Your partner')}
          </div>
          {e.progress?.current_eta && (
            <div className="text-[11px] text-text-muted inline-flex items-center gap-1 mt-1">
              <Clock size={10} weight="bold" />
              Expected by {new Date(e.progress.current_eta).toLocaleDateString('en-IN', { day: 'numeric', month: 'short' })}
            </div>
          )}
        </div>

        <Link to={href} data-testid="active-engagements-banner-cta"
          className="text-[13px] font-semibold px-3.5 py-2 rounded-lg text-white inline-flex items-center gap-1.5 flex-shrink-0"
          style={{ backgroundColor: accent }}>
          {meta.cta} <ArrowRight size={11} weight="bold" />
        </Link>

        {slides.length > 1 && (
          <div className="flex items-center gap-1 flex-shrink-0">
            <button onClick={() => step(-1)} aria-label="Previous engagement"
              data-testid="active-engagements-banner-prev"
              className="w-6 h-6 rounded-md border border-border text-text-muted hover:text-text-primary inline-flex items-center justify-center">
              <CaretLeft size={10} weight="bold" />
            </button>
            <button onClick={() => step(1)} aria-label="Next engagement"
              data-testid="active-engagements-banner-next"
              className="w-6 h-6 rounded-md border border-border text-text-muted hover:text-text-primary inline-flex items-center justify-center">
              <CaretRight size={10} weight="bold" />
            </button>
          </div>
        )}
      </div>

      {slides.length > 1 && (
        <div className="px-3.5 pb-2.5 flex items-center gap-1.5" data-testid="active-engagements-banner-dots">
          {slides.map((s, i) => (
            <button key={s.id} onClick={() => { setPaused(true); setIdx(i); }}
              aria-label={`Show engagement ${i + 1}`}
              aria-current={i === idx}
              data-testid={`active-engagements-banner-dot-${i}`}
              className="h-1 rounded-full transition-all"
              style={{ width: i === idx ? 18 : 6, backgroundColor: i === idx ? accent : 'var(--border)' }} />
          ))}
        </div>
      )}
    </div>
  );
};

export default ActiveEngagementsBanner;
